/**
 * 中央工作区（重构版）
 *
 * 职责：编排各子模块，不包含具体业务逻辑。
 * 子模块：
 *  - hooks/useKeyboardShortcuts  → 键盘快捷键
 *  - hooks/useCanvasViewport     → 滚轮缩放 + 视口平移（isSpacePressed / handPanToolActive 由根层注入）
 *  - hooks/useBgImageDrag        → 背景图拖拽
 *  - GridGuideLayer              → 栅格辅助线渲染
 *  - TextEditOverlay             → 文本双击编辑浮层
 *  - 原有 hooks                  → useElementDrag/useResize/useLineHandle/useRadiusDrag/useRotate
 */

import { useRef, useCallback, useEffect, useState, useMemo, useLayoutEffect } from 'react';
import { flushSync } from 'react-dom';
import { useDroppable } from '@dnd-kit/core';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';
import { useCanvasStore } from '@/components/canvas/store/canvas.store';
import type {
  Element,
  GroupElement as GroupElementType,
  TextElement as TextElementType,
  ImageElement as ImageElementType,
  LineElement as LineElementType,
  TableElement as TableElementType,
} from '@/components/canvas/document/publicTypes';
import { resolvePageGuideSettings } from '@/components/canvas/page/guideSettings';
import { computeGridGuideLines } from '@/components/canvas/workspace/overlays/gridGuides';
import { getMergedRangeContaining } from '@/components/canvas/elements/table/utils/tableUtilsIndex';
import { cn } from '@heroui/react';
import { useWorkspaceChromeRegistration } from '@/components/canvas/workspace/stage/WorkspaceChromeContext';
import { useKeyboardShortcuts } from '@/components/canvas/workspace/controllers/useKeyboardShortcuts';
import { useCanvasViewport } from '@/components/canvas/workspace/viewport/useCanvasViewport';
import { useBgImageDrag } from '@/components/canvas/workspace/controllers/useBgImageDrag';
import { useMarqueeSelect } from '@/components/canvas/workspace/controllers/useMarqueeSelect';
import { useTextDraw } from '@/components/canvas/elements/text/controllers/useTextDraw';
import { useShapeDraw } from '@/components/canvas/elements/shape/controllers/useShapeDraw';
import { useTableDraw } from '@/components/canvas/elements/table/controllers/useTableDraw';
import { useWorkspaceFitToScreen } from '@/components/canvas/workspace/viewport/useWorkspaceFitToScreen';
import { useWorkspaceFullscreen } from '@/components/canvas/workspace/viewport/useWorkspaceFullscreen';
import { useWorkspacePageNavigationKeyboard } from '@/components/canvas/page/controllers/useWorkspacePageNavigationKeyboard';
import { registerWorkspacePageFitRunner } from '@/components/canvas/workspace/viewport/workspacePageFitController';
import { relayoutPagesForVerticalZoom } from '@/components/canvas/workspace/model/pageArrangement';
import { usePresentationKeyboard } from '@/components/canvas/workspace/viewport/usePresentationKeyboard';
import { useWorkspaceInteraction } from '@/components/canvas/workspace/controllers/useWorkspaceInteraction';
import { useBlockInteraction } from '@/components/canvas/blocks/useBlockInteraction';
import { usePageDrag } from '@/components/canvas/workspace/controllers/usePageDrag';
import { useViewportPageVisibility } from '@/components/canvas/page/controllers/useViewportPageVisibility';
import { useVerticalLayoutZoomSpacing } from '@/components/canvas/workspace/controllers/useVerticalLayoutZoomSpacing';
import { getSingleModeVisiblePageIds } from '@/components/canvas/workspace/model/pageArrangement';
import { screenToWorld } from '@/components/canvas/document/coordinates';
import { findPageAtWorldPoint, isPointOnPageChrome } from '@/components/canvas/workspace/model/resolveDrawSurface';
import { PagesLayer } from './layers/PagesLayer';
import { WorkspaceStage } from '@/components/canvas/workspace/stage/WorkspaceStage';
import { PlaybackLayer } from './layers/PlaybackLayer';
import { CanvasContextMenu } from './overlays/CanvasContextMenu';
import type { WorkspaceProps } from './types';

export type { WorkspaceProps, WorkspacePlayConfig } from './types';

export function Workspace({
  className,
  autoEnterFullscreen = false,
  readonly = false,
  playConfig,
  onExitComponent,
  textDrawMode = false,
  isSpacePressed,
  handPanToolActive,
  viewportPanDragEndRef,
  fitCurrentPageOnMount = true,
}: WorkspaceProps) {
  const workspaceRef = useRef<HTMLDivElement>(null);
  const stageHostRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const activePageRef = useRef<HTMLDivElement>(null);

  const { setNodeRef: setDroppableRef } = useDroppable({ id: 'workspace-drop' });

  const {
    getCurrentPage,
    pages,
    workspaceElements,
    currentPageId,
    setCurrentPageId,
    selectedElementIds,
    selectElement,
    clearSelection,
    deactivateCurrentPage,
    selectPageFrame,
    isPageSelected,
    applyPageFrameResizeSilent,
    commitPageFrameResize,
    zoom,
    panX,
    panY,
    setZoom,
    setPan,
    fitToScreen,
    setPageBackground,
    activePageDomRef,
    stageHostDomRef,
    setElementPositions,
    setWorkspaceElementPositions,
    commitElementDrag,
    deleteSelectedElements,
    copySelectedElements,
    cutSelectedElements,
    pasteElements,
    pasteFromSystemClipboard,
    hasClipboard,
    undo,
    redo,
    updateElement,
    updateElementSilent,
    pushHistory,
    cancelPendingHistory,
    bringToFront,
    sendToBack,
    bringForward,
    sendBackward,
    groupElements,
    ungroupElement,
    commitGroupResize,
    selectedPageIds,
    selectionScope,
    selectMarqueeResult,
    updateElementSilentInDocument,
    commitMultiSelectionResize,
    editingTextId,
    setEditingTextId,
    editingGroupId,
    setEditingGroupId,
    penToolActive,
    setPenToolActive,
    freehandToolActive,
    eraserToolActive,
    addTextElementAt,
    addParagraphTextAt,
    addShapeElementAt,
    addTableElementAt,
    textDrawMode: storeTextDrawMode,
    setTextDrawMode,
    tableDrawMode: storeTableDrawMode,
    setTableDrawMode,
    shapeDrawKind,
    setShapeDrawKind,
    selectElements,
    selectedTableColumn,
    selectedTableRow,
    selectedTableCells,
    setSelectedTableCells,
    editingTableCellId,
    setEditingTableCellId,
    updatePageWorkspaceLayout,
    updatePageWorkspaceLayoutLive,
    bringPageToFront,
    workspacePageLayoutMode,
    selectedBlockIds,
    selectBlock,
    hoveredBlockId,
    setHoveredBlockId,
    updateBlockSilent,
    commitBlockLayout,
    setBlockPositions,
  } = useCanvas();

  useLayoutEffect(() => {
    stageHostDomRef.current = stageHostRef.current;
    setDroppableRef(stageHostRef.current);
    return () => setDroppableRef(null);
  });

  const page = getCurrentPage();

  const verticalPageLayoutKey = useMemo(
    () => pages.map((p) => `${p.id}:${p.size.width}x${p.size.height}`).join('|'),
    [pages],
  );
  useVerticalLayoutZoomSpacing(workspacePageLayoutMode, zoom, verticalPageLayoutKey);

  const { contentSize, fitTransition, getFitSize, handleFitToScreenRequest, runFitWithTransition } =
    useWorkspaceFitToScreen({
      workspaceRef,
      stageHostRef,
      page,
      fitToScreen,
      fitCurrentPageOnMount,
    });

  useEffect(() => {
    registerWorkspacePageFitRunner(({ animated }) => {
      const size = getFitSize();
      if (!size) return;

      const applyFit = () => {
        if (animated) {
          runFitWithTransition(size.w, size.h);
        } else {
          fitToScreen(size.w, size.h, { chromeInsets: 'edit' });
        }
      };

      applyFit();

      const st = useCanvasStore.getState();
      if (st.workspacePageLayoutMode !== 'vertical' || st.pages.length === 0) return;
      const nextPages = relayoutPagesForVerticalZoom(st.pages, st.zoom);
      const yChanged = nextPages.some(
        (p, i) => p.workspaceLayout.y !== st.pages[i]?.workspaceLayout.y,
      );
      if (yChanged) {
        useCanvasStore.setState({ pages: nextPages });
        applyFit();
      }
    });
    return () => registerWorkspacePageFitRunner(null);
  }, [getFitSize, runFitWithTransition, fitToScreen]);

  const {
    isFullscreen,
    playbackBarVisible,
    setPlaybackBarVisible,
    autoPlay,
    setAutoPlay,
    loop,
    setLoop,
    playbackIntervalSeconds,
    setPlaybackIntervalSeconds,
    handleToggleFullscreen,
    handlePreviousCanvas,
    handleNextCanvas,
  } = useWorkspaceFullscreen({
    workspaceRef,
    stageHostRef,
    pages,
    currentPageId,
    setCurrentPageId,
    autoEnterFullscreen,
    playConfig,
    getFitSize,
    runFitWithTransition,
  });

  const isReadonlyMode = readonly || isFullscreen;

  const workspaceChromeSnapshot = useMemo(
    () => ({
      contentSize,
      onFitToScreenRequest: handleFitToScreenRequest,
      isFullscreen,
      isReadonly: isReadonlyMode,
      onToggleFullscreen: handleToggleFullscreen,
      canEnterFullscreen: pages.length > 0,
    }),
    [
      contentSize,
      handleFitToScreenRequest,
      isFullscreen,
      isReadonlyMode,
      handleToggleFullscreen,
      pages.length,
    ],
  );
  useWorkspaceChromeRegistration(workspaceChromeSnapshot);

  useEffect(() => {
    if (!isFullscreen) return;
    clearSelection();
    setEditingTextId(null);
    setEditingGroupId(null);
    setEditingTableCellId(null);
  }, [isFullscreen, clearSelection, setEditingTextId, setEditingGroupId, setEditingTableCellId]);

  usePresentationKeyboard({
    active: isFullscreen,
    onStartAutoPlay: () => setAutoPlay(true),
    onStopAutoPlay: () => setAutoPlay(false),
    onPreviousPage: handlePreviousCanvas,
    onNextPage: handleNextCanvas,
  });

  useWorkspacePageNavigationKeyboard({ enabled: !isFullscreen });

  /** 双击图片后进入「移动/缩放图片」模式；为 true 时关闭画布滚轮缩放，滚轮仅驱动图片 imageScale */
  const [editingImagePosition, setEditingImagePosition] = useState<{
    id: string;
    startClientX: number;
    startClientY: number;
    startObjectPositionX: number;
    startObjectPositionY: number;
  } | null>(null);

  // ── viewport interactions ─────────────────────────────────────────────────

  const {
    setIsDragging,
    panCursor,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp: handleViewportMouseUp,
  } = useCanvasViewport({
    workspaceRef,
    zoom,
    panX,
    panY,
    setZoom,
    setPan,
    wheelZoomDisabled: !!editingImagePosition,
    wheelPanDisabled: isReadonlyMode,
    isSpacePressed: isReadonlyMode ? false : isSpacePressed,
    handPanToolActive: isReadonlyMode ? false : handPanToolActive,
  });

  useEffect(() => {
    viewportPanDragEndRef.current = () => setIsDragging(false);
    return () => {
      viewportPanDragEndRef.current = () => {};
    };
  }, [setIsDragging, viewportPanDragEndRef]);

  const isPanModeActive = useMemo(
    () => !isReadonlyMode && (isSpacePressed || handPanToolActive),
    [isReadonlyMode, isSpacePressed, handPanToolActive],
  );

  const getPageById = useCallback(
    (pageId: string) => useCanvasStore.getState().pages.find((p) => p.id === pageId),
    [],
  );

  const visiblePageIds = useViewportPageVisibility(
    pages,
    currentPageId,
    stageHostRef,
    panX,
    panY,
    zoom,
  );

  const { draggingPageId, startPageDrag, handleChromeMouseDown, handleChromeClick } = usePageDrag({
    zoom,
    disabled: isReadonlyMode,
    isPanModeActive,
    currentPageId,
    setCurrentPageId,
    setEditingTextId,
    setEditingGroupId,
    bringPageToFront,
    selectPageFrame,
    pushHistory,
    updatePageWorkspaceLayoutLive,
    updatePageWorkspaceLayout,
    getPageById,
  });

  const effectiveVisiblePageIds = useMemo(() => {
    if (workspacePageLayoutMode === 'single') {
      return getSingleModeVisiblePageIds(pages, currentPageId, draggingPageId);
    }
    const ids = new Set(visiblePageIds);
    if (draggingPageId) ids.add(draggingPageId);
    return ids;
  }, [workspacePageLayoutMode, pages, currentPageId, visiblePageIds, draggingPageId]);

  // 无当前页时提供安全默认值，保证后续 hooks 数量不变
  const size = page?.size ?? { width: 800, height: 600 };
  const background = page?.background ?? { type: 'color' as const, value: '#ffffff' };
  const elements = page?.elements ?? [];
  const guideSettings = resolvePageGuideSettings(page);
  const gridGuides = useMemo(() => {
    if (!guideSettings.enabled || guideSettings.system !== 'grid') {
      return { horizontal: [], vertical: [] };
    }
    return computeGridGuideLines(size.width, size.height, guideSettings.grid);
  }, [guideSettings, size.width, size.height]);
  // 稳定访问 elements（供 exitGroupEditMode 等 callback 使用，避免过期闭包）
  const elementsRef = useRef(elements);
  elementsRef.current = elements;

  const { isMovingBgImage, setIsMovingBgImage, startBgImageDrag } = useBgImageDrag({
    background,
    pageRef: activePageRef,
    setPageBackground,
  });

  // ── marquee (圈选) ────────────────────────────────────────────────────────

  const marqueePages = useMemo(
    () =>
      pages
        .filter((p) => effectiveVisiblePageIds.has(p.id))
        .map((p) => ({
          id: p.id,
          workspaceLayout: p.workspaceLayout,
          size: p.size,
          elements: p.elements,
        })),
    [pages, effectiveVisiblePageIds],
  );

  const effectiveTextDrawMode = !!storeTextDrawMode || textDrawMode;
  const effectiveShapeDrawMode = !!shapeDrawKind;
  const effectiveTableDrawMode = storeTableDrawMode;

  const beginTextEditRef = useRef<
    (id: string, opts?: import('../elements/types').TextEditOptions) => void
  >(() => {});

  const { drawRect: textDrawRect, handleCanvasMouseDown: handleTextDrawMouseDown, isDrawing: isTextDrawing } =
    useTextDraw({
      active: effectiveTextDrawMode,
      blockKind: storeTextDrawMode ?? undefined,
      pageRef: activePageRef,
      stageHostRef,
      panX,
      panY,
      zoom,
      isSpacePressed: isPanModeActive,
      addTextElementAt,
      addParagraphTextAt,
      beginTextEdit: (id, opts) => beginTextEditRef.current(id, opts),
      onDrawComplete: () => setTextDrawMode(null),
    });

  const {
    preview: shapeDrawPreview,
    handleCanvasMouseDown: handleShapeDrawMouseDown,
    isDrawing: isShapeDrawing,
  } = useShapeDraw({
    kind: shapeDrawKind,
    pageRef: activePageRef,
    stageHostRef,
    panX,
    panY,
    zoom,
    isSpacePressed: isPanModeActive,
    addShapeElementAt,
    onDrawComplete: () => setShapeDrawKind(null),
  });

  const {
    drawRect: tableDrawRect,
    pending: tableDrawPending,
    setPendingRows: setTableDrawRows,
    setPendingCols: setTableDrawCols,
    handleCanvasMouseDown: handleTableDrawMouseDown,
    confirmPending: confirmTableDraw,
    cancelPending: cancelTableDraw,
    isDrawing: isTableDrawing,
    isPending: isTableDrawPending,
  } = useTableDraw({
    active: effectiveTableDrawMode,
    pageRef: activePageRef,
    stageHostRef,
    panX,
    panY,
    zoom,
    isSpacePressed: isPanModeActive,
    addTableElementAt,
    onDrawComplete: () => setTableDrawMode(false),
  });

  const {
    marquee,
    handleCanvasMouseDown: handleMarqueeMouseDown,
    handleStageMarqueeMouseDown,
    isMarqueeActive,
  } = useMarqueeSelect({
    workspaceRef,
    zoom,
    panX,
    panY,
    pages: marqueePages,
    workspaceElements,
    currentPageId,
    selectMarqueeResult,
    clearSelection,
    onPageBlankClick: selectPageFrame,
    isSpacePressed: isPanModeActive,
    penToolActive,
    freehandToolActive,
    eraserToolActive,
    tableDrawMode: effectiveTableDrawMode,
    tableDrawPending: isTableDrawPending,
    editingGroupId,
  });

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isReadonlyMode) return;
      if (e.button !== 0) return;
      if ((e.target as HTMLElement).closest('[data-block-hit]')) return;
      if (isTableDrawPending) return;
      if (effectiveShapeDrawMode && handleShapeDrawMouseDown(e)) return;
      if (effectiveTextDrawMode && handleTextDrawMouseDown(e)) return;
      if (effectiveTableDrawMode && handleTableDrawMouseDown(e)) return;
      const pageHasRootElements =
        (page?.elements ?? []).some(
          (el) => el.interaction.visible !== false && !el.layout.parentId,
        ) ?? false;
      // 页内有子元素时禁止板面空白拖页（与圈选冲突）；仅空页可在选中后拖移
      if (
        isPageSelected &&
        selectedElementIds.length === 0 &&
        !pageHasRootElements &&
        !isPanModeActive &&
        !penToolActive &&
        !freehandToolActive &&
        !eraserToolActive
      ) {
        const target = e.target as HTMLElement;
        if (
          target.closest('[data-page]') &&
          !target.closest('[data-handle]') &&
          !target.closest('[data-element-hit]') &&
          !target.closest('[data-block-hit]') &&
          !target.closest('[data-page-chrome]') &&
          !target.closest('[data-page-frame-drag]')
        ) {
          if (page) {
            startPageDrag(page.id, e);
            return;
          }
        }
      }
      handleMarqueeMouseDown(e);
    },
    [
      effectiveShapeDrawMode,
      effectiveTextDrawMode,
      effectiveTableDrawMode,
      isTableDrawPending,
      handleShapeDrawMouseDown,
      handleTextDrawMouseDown,
      handleTableDrawMouseDown,
      handleMarqueeMouseDown,
      isPageSelected,
      selectedElementIds.length,
      isPanModeActive,
      penToolActive,
      freehandToolActive,
      eraserToolActive,
      page,
      startPageDrag,
      page?.elements,
      isReadonlyMode,
    ],
  );

  const handleInactivePagePointerDown = useCallback(
    (pageId: string, e: React.MouseEvent) => {
      if (isReadonlyMode) return;
      if (e.button !== 0 || isPanModeActive) return;
      e.stopPropagation();
      e.preventDefault();

      if (
        effectiveTextDrawMode ||
        effectiveShapeDrawMode ||
        effectiveTableDrawMode ||
        penToolActive ||
        freehandToolActive
      ) {
        bringPageToFront(pageId);
        setCurrentPageId(pageId);
        setEditingTextId(null);
        setEditingGroupId(null);
        const pageNode = (e.target as HTMLElement).closest('[data-page]') as HTMLDivElement | null;
        if (pageNode) {
          activePageRef.current = pageNode;
          if (activePageDomRef) activePageDomRef.current = pageNode;
        }
        if (effectiveShapeDrawMode && handleShapeDrawMouseDown(e)) return;
        if (effectiveTextDrawMode && handleTextDrawMouseDown(e)) return;
        if (effectiveTableDrawMode && handleTableDrawMouseDown(e)) return;
        return;
      }

      const target = e.target as HTMLElement;
      const blockHit = target.closest('[data-block-hit]');
      const blockId = blockHit?.getAttribute('data-block-id');
      const hit = target.closest('[data-element-hit]');
      const elementId = hit?.getAttribute('data-element-id');

      bringPageToFront(pageId);
      setCurrentPageId(pageId);
      setEditingTextId(null);
      setEditingGroupId(null);

      if (blockId) {
        selectBlock(blockId, false);
      } else if (elementId) {
        selectElement(elementId, false);
      } else {
        selectPageFrame();
      }
    },
    [
      effectiveTextDrawMode,
      effectiveShapeDrawMode,
      effectiveTableDrawMode,
      penToolActive,
      freehandToolActive,
      isPanModeActive,
      setCurrentPageId,
      selectElement,
      selectPageFrame,
      selectBlock,
      setEditingTextId,
      setEditingGroupId,
      bringPageToFront,
      handleShapeDrawMouseDown,
      handleTextDrawMouseDown,
      handleTableDrawMouseDown,
      activePageDomRef,
      isReadonlyMode,
    ],
  );

  const baseInteraction = useWorkspaceInteraction({
    elements,
    gridGuides,
    background,
    pageRef: activePageRef,
    stageHostRef,
    zoom,
    panX,
    panY,
    selectedElementIds,
    selectElement,
    selectElements,
    clearSelection,
    deactivateCurrentPage,
    selectPageFrame,
    isPageSelected,
    applyPageFrameResizeSilent,
    commitPageFrameResize,
    pageSize: size,
    getPage: () => page ?? null,
    editingTextId,
    setEditingTextId,
    editingGroupId,
    setEditingGroupId,
    updateElement,
    updateElementSilent,
    pushHistory,
    cancelPendingHistory,
    setElementPositions,
    setWorkspaceElementPositions,
    commitElementDrag,
    commitGroupResize,
    penToolActive,
    freehandToolActive,
    eraserToolActive,
    isPanModeActive,
    isMovingBgImage,
    setIsMovingBgImage,
    handleViewportMouseUp,
    selectedTableCells,
    setSelectedTableCells,
    editingTableCellId,
    setEditingTableCellId,
    selectedTableColumn,
    selectedTableRow,
    effectiveTextDrawMode,
    effectiveShapeDrawMode,
    effectiveTableDrawMode,
    isMarqueeActive,
    marquee,
    isTextDrawing,
    textDrawRect,
    isShapeDrawing,
    shapeDrawPreview,
    isTableDrawing,
    tableDrawRect,
    tableDrawPending,
    setTableDrawRows,
    setTableDrawCols,
    confirmTableDraw,
    cancelTableDraw,
    panCursor: panCursor ?? null,
    handleCanvasMouseDown,
    startBgImageDrag,
    isPresentationMode: isFullscreen,
    isReadonlyMode,
    onPageFrameDragStart: (e: React.MouseEvent) => {
      if (isFullscreen) return;
      if (isReadonlyMode) return;
      if (page) startPageDrag(page.id, e);
    },
    pages,
    workspaceElements,
    currentPageId,
    setCurrentPageId,
    bringPageToFront,
    selectedPageIds,
    selectionScope,
    updateElementSilentInDocument,
    commitMultiSelectionResize,
    draggingPageId,
    workspacePageLayoutMode,
  });

  const pageBlocks = page?.blocks ?? [];

  const blockInteraction = useBlockInteraction({
    blocks: pageBlocks,
    zoom,
    pageSnapRef: baseInteraction.pageSnapRef,
    selectedBlockIds,
    selectBlock,
    clearSelection,
    setBlockPositions,
    updateBlockSilent,
    commitBlockLayout,
    pushHistory,
    cancelPendingHistory,
    isPresentationMode: isReadonlyMode,
    isPanModeActive,
  });

  const interaction = useMemo(
    () => ({
      ...baseInteraction,
      ...blockInteraction,
      alignmentGuides: blockInteraction.blockDrag
        ? blockInteraction.blockAlignmentGuides
        : baseInteraction.alignmentGuides,
      selectedBlockIds,
      hoveredBlockId,
      setHoveredBlockId,
      pageBlocks,
    }),
    [
      baseInteraction,
      blockInteraction,
      blockInteraction.blockDrag,
      blockInteraction.blockAlignmentGuides,
      selectedBlockIds,
      hoveredBlockId,
      setHoveredBlockId,
      pageBlocks,
    ],
  );

  beginTextEditRef.current = interaction.beginTextEdit;

  const workspaceZoomShortcuts = useMemo(
    () => ({
      getZoom: () => useCanvasStore.getState().zoom,
      setZoom,
      onZoomFit: handleFitToScreenRequest,
    }),
    [setZoom, handleFitToScreenRequest],
  );

  useKeyboardShortcuts({
    selectedCount: selectedElementIds.length + selectedPageIds.length + selectedBlockIds.length,
    deleteSelectedElements: isReadonlyMode ? () => {} : deleteSelectedElements,
    undo: isReadonlyMode ? () => {} : undo,
    redo: isReadonlyMode ? () => {} : redo,
    copySelectedElements,
    cutSelectedElements: isReadonlyMode ? () => false : cutSelectedElements,
    pasteFromSystemClipboard: isReadonlyMode ? async () => false : pasteFromSystemClipboard,
    onEscape: interaction.exitGroupEditMode,
    workspaceZoom: isFullscreen ? undefined : workspaceZoomShortcuts,
  });

  const enteringTableEditForRef = useRef<string | null>(null);
  useEffect(() => {
    if (editingTableCellId === selectedTableCells?.elementId) enteringTableEditForRef.current = null;
    if (!selectedTableCells || editingTableCellId === selectedTableCells.elementId) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (enteringTableEditForRef.current === selectedTableCells.elementId) return;
      const target = e.target as Node | null;
      if (
        target &&
        (target instanceof HTMLTextAreaElement ||
          target instanceof HTMLInputElement ||
          (target instanceof HTMLElement && target.getAttribute('contenteditable') === 'true'))
      ) {
        return;
      }
      const el = elementsRef.current.find((x) => x.id === selectedTableCells.elementId);
      if (el?.type !== 'table') return;
      const rows = Math.max(1, (el as import('@/components/canvas/document/publicTypes').TableElement).data.rows);
      const cols = Math.max(1, (el as import('@/components/canvas/document/publicTypes').TableElement).data.cols);
      const { r0, c0 } = selectedTableCells;

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        let nr = r0;
        let nc = c0;
        if (e.key === 'ArrowLeft') nc = Math.max(0, c0 - 1);
        else if (e.key === 'ArrowRight') nc = Math.min(cols - 1, c0 + 1);
        else if (e.key === 'ArrowUp') nr = Math.max(0, r0 - 1);
        else nr = Math.min(rows - 1, r0 + 1);
        const tableEl = el as import('@/components/canvas/document/publicTypes').TableElement;
        const merged = getMergedRangeContaining(tableEl, nr, nc);
        if (merged) {
          const [mr0, mc0, mr1, mc1] = merged;
          setSelectedTableCells({
            elementId: selectedTableCells.elementId,
            r0: mr0,
            c0: mc0,
            r1: mr1,
            c1: mc1,
          });
        } else {
          setSelectedTableCells({
            elementId: selectedTableCells.elementId,
            r0: nr,
            c0: nc,
            r1: nr,
            c1: nc,
          });
        }
        return;
      }

    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [selectedTableCells, editingTableCellId, setSelectedTableCells, setEditingTableCellId]);

  const handleStageMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (isReadonlyMode) {
        handleMouseDown(e);
        return;
      }
      if (e.button !== 0) return;
      if ((e.target as HTMLElement).closest('[data-block-hit]')) return;
      if (penToolActive || freehandToolActive) {
        const target = e.target as HTMLElement;
        if (target.closest('[data-page-chrome]')) {
          return;
        }
        const host = stageHostRef.current;
        if (host) {
          const world = screenToWorld(
            e.clientX,
            e.clientY,
            host.getBoundingClientRect(),
            panX,
            panY,
            zoom,
          );
          const hitPage = findPageAtWorldPoint(world.x, world.y, pages);
          if (hitPage && !isPointOnPageChrome(world.x, world.y, hitPage, zoom)) {
            return;
          }
        }
        if (
          !target.closest('[data-page]') &&
          !target.closest('[data-element-hit]') &&
          !target.closest('[data-block-hit]') &&
          !target.closest('[data-handle]')
        ) {
          if (currentPageId) {
            deactivateCurrentPage();
            activePageRef.current = null;
            if (activePageDomRef) activePageDomRef.current = null;
          }
          return;
        }
      }

      if (effectiveTextDrawMode || effectiveShapeDrawMode || effectiveTableDrawMode) {
        const target = e.target as HTMLElement;
        if (
          !target.closest('[data-page]') &&
          !target.closest('[data-page-chrome]') &&
          !target.closest('[data-element-hit]') &&
          !target.closest('[data-block-hit]') &&
          !target.closest('[data-handle]') &&
          !target.closest('[data-table-draw-toolbar]') &&
          !target.closest('[data-table-axis-controls]')
        ) {
          if (currentPageId) {
            deactivateCurrentPage();
            activePageRef.current = null;
            if (activePageDomRef) activePageDomRef.current = null;
          }
          if (effectiveShapeDrawMode && handleShapeDrawMouseDown(e)) return;
          if (effectiveTextDrawMode && handleTextDrawMouseDown(e)) return;
          if (effectiveTableDrawMode && handleTableDrawMouseDown(e)) return;
        }
      }
      handleMouseDown(e);
      handleStageMarqueeMouseDown(e);
    },
    [
      effectiveTextDrawMode,
      effectiveShapeDrawMode,
      effectiveTableDrawMode,
      penToolActive,
      freehandToolActive,
      pages,
      panX,
      panY,
      zoom,
      currentPageId,
      deactivateCurrentPage,
      handleShapeDrawMouseDown,
      handleTextDrawMouseDown,
      handleTableDrawMouseDown,
      handleMouseDown,
      handleStageMarqueeMouseDown,
      activePageDomRef,
      isReadonlyMode,
    ],
  );

  const stageStyle: React.CSSProperties = {
    transform: `translate(${panX}px, ${panY}px) scale(${zoom})`,
    transformOrigin: '0 0',
    ...(fitTransition && { transition: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)' }),
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div
      ref={workspaceRef}
      className={cn('absolute inset-0 min-h-0 min-w-0 overflow-hidden bg-background', className)}
      style={{ outline: 'none' }}
    >
      <CanvasContextMenu interaction={interaction}>
        <WorkspaceStage
          stageHostRef={stageHostRef}
          stageRef={stageRef}
          stageStyle={stageStyle}
          zoom={zoom}
          interaction={interaction}
          onStageMouseDown={handleStageMouseDown}
          worldMarquee={isMarqueeActive ? marquee : null}
          onStageMouseMove={handleMouseMove}
          onStageBackgroundClick={interaction.handleContentBackgroundClick}
        >
          <PagesLayer
            pages={pages}
            currentPageId={currentPageId}
            visiblePageIds={effectiveVisiblePageIds}
            workspaceElements={workspaceElements}
            activePageRef={activePageRef}
            activePageDomRef={activePageDomRef}
            interaction={interaction}
            selectedElementIds={selectedElementIds}
            selectedPageIds={selectedPageIds}
            hoveredElementId={interaction.hoveredElementId}
            draggingPageId={draggingPageId}
            onPageChromeMouseDown={handleChromeMouseDown}
            onPageChromeClick={handleChromeClick}
            onInactivePagePointerDown={handleInactivePagePointerDown}
            zoom={zoom}
          />
        </WorkspaceStage>
      </CanvasContextMenu>


      <PlaybackLayer
        isFullscreen={isFullscreen}
        playbackBarVisible={playbackBarVisible}
        onPlaybackBarVisibleChange={setPlaybackBarVisible}
        currentPageIndex={Math.max(1, pages.findIndex((c) => c.id === currentPageId) + 1)}
        totalPages={Math.max(1, pages.length)}
        onPrev={handlePreviousCanvas}
        onNext={handleNextCanvas}
        autoPlay={autoPlay}
        onAutoPlayChange={setAutoPlay}
        loop={loop}
        onLoopChange={setLoop}
        playbackIntervalSeconds={playbackIntervalSeconds}
        onPlaybackIntervalChange={setPlaybackIntervalSeconds}
        onExitComponent={onExitComponent}
      />

    </div>
  );
}
